import React, { useState } from "react";
import { buildApiUrl } from "../config/api";
import "./auth/auth.css";

const MODES = {
    LOGIN: "login",
    REGISTER: "register",
    FORGOT: "forgot"
};

const initialRegisterState = {
    username: "",
    email: "",
    password: ""
};

const initialLoginState = {
    identifier: "",
    password: ""
};

const AuthForm = ({ onLogin }) => {
    const [mode, setMode] = useState(MODES.LOGIN);
    const [loginData, setLoginData] = useState(initialLoginState);
    const [registerData, setRegisterData] = useState(initialRegisterState);
    const [forgotEmail, setForgotEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    const resetMessages = () => {
        setError("");
        setSuccess("");
    };

    const switchMode = (nextMode) => {
        setMode(nextMode);
        resetMessages();
        if (nextMode === MODES.LOGIN) {
            setLoginData(initialLoginState);
        }
        if (nextMode === MODES.FORGOT) {
            setForgotEmail("");
        }
    };

    const handleLoginChange = (event) => {
        const { name, value } = event.target;
        setLoginData((prev) => ({ ...prev, [name]: value }));
        resetMessages();
    };

    const handleRegisterChange = (event) => {
        const { name, value } = event.target;
        setRegisterData((prev) => ({ ...prev, [name]: value }));
        resetMessages();
    };

    const handleForgotChange = (event) => {
        setForgotEmail(event.target.value);
        resetMessages();
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setLoading(true);
        resetMessages();

        try {
            if (mode === MODES.LOGIN) {
                const response = await fetch(buildApiUrl("/api/auth/login"), {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(loginData)
                });
                const data = await response.json();
                if (response.ok) {
                    onLogin(data.user, data.access_token);
                } else {
                    setError(data.error || "登录失败，请稍后再试。");
                }
            } else if (mode === MODES.REGISTER) {
                const response = await fetch(buildApiUrl("/api/auth/register"), {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(registerData)
                });
                const data = await response.json();
                if (response.ok) {
                    onLogin(data.user, data.access_token);
                } else {
                    setError(data.error || "注册失败，请稍后再试。");
                }
            } else {
                const response = await fetch(buildApiUrl("/api/auth/password/forgot"), {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ email: forgotEmail })
                });
                if (response.ok) {
                    setSuccess("如果邮箱存在，我们已发送重置链接，请检查收件箱。");
                    setForgotEmail("");
                } else {
                    const data = await response.json();
                    setError(data.error || "无法发送重置邮件，请稍后再试。");
                }
            }
        } catch (err) {
            setError("网络异常，请稍后重试。");
        } finally {
            setLoading(false);
        }
    };

    const isLogin = mode === MODES.LOGIN;
    const isRegister = mode === MODES.REGISTER;
    const isForgot = mode === MODES.FORGOT;

    return (
        <div className="auth-panel">
            <header className="auth-panel__header">
                <span className="auth-panel__brand">DoneLog</span>
                {isLogin && <h2>欢迎回来</h2>}
                {isRegister && <h2>加入 DoneLog</h2>}
                {isForgot && <h2>重置密码</h2>}
                {isLogin && <p>输入用户名或邮箱，继续你的目标旅程。</p>}
                {isRegister && <p>注册账号体验智能拆解与进度追踪。</p>}
                {isForgot && <p>输入注册邮箱，我们会发送一封重置密码的邮件。</p>}
            </header>

            {error && <div className="auth-panel__message auth-panel__message--error">{error}</div>}
            {success && <div className="auth-panel__message auth-panel__message--success">{success}</div>}

            <form className="auth-panel__form" onSubmit={handleSubmit}>
                {isLogin && (
                    <>
                        <div className="auth-panel__field">
                            <label htmlFor="login-identifier">用户名或邮箱</label>
                            <input
                                id="login-identifier"
                                type="text"
                                name="identifier"
                                className="auth-panel__input"
                                placeholder="例如：alice 或 alice@example.com"
                                value={loginData.identifier}
                                onChange={handleLoginChange}
                                required
                                disabled={loading}
                            />
                        </div>
                        <div className="auth-panel__field">
                            <label htmlFor="login-password">密码</label>
                            <input
                                id="login-password"
                                type="password"
                                name="password"
                                className="auth-panel__input"
                                placeholder="不少于 6 位字符"
                                value={loginData.password}
                                onChange={handleLoginChange}
                                required
                                disabled={loading}
                            />
                        </div>
                    </>
                )}

                {isRegister && (
                    <>
                        <div className="auth-panel__field">
                            <label htmlFor="register-username">用户名</label>
                            <input
                                id="register-username"
                                type="text"
                                name="username"
                                className="auth-panel__input"
                                placeholder="例如：alice"
                                value={registerData.username}
                                onChange={handleRegisterChange}
                                required
                                disabled={loading}
                            />
                        </div>
                        <div className="auth-panel__field">
                            <label htmlFor="register-email">邮箱</label>
                            <input
                                id="register-email"
                                type="email"
                                name="email"
                                className="auth-panel__input"
                                placeholder="you@example.com"
                                value={registerData.email}
                                onChange={handleRegisterChange}
                                required
                                disabled={loading}
                            />
                        </div>
                        <div className="auth-panel__field">
                            <label htmlFor="register-password">密码</label>
                            <input
                                id="register-password"
                                type="password"
                                name="password"
                                className="auth-panel__input"
                                placeholder="不少于 6 位字符"
                                value={registerData.password}
                                onChange={handleRegisterChange}
                                required
                                disabled={loading}
                            />
                        </div>
                    </>
                )}

                {isForgot && (
                    <div className="auth-panel__field">
                        <label htmlFor="forgot-email">注册邮箱</label>
                        <input
                            id="forgot-email"
                            type="email"
                            name="email"
                            className="auth-panel__input"
                            placeholder="you@example.com"
                            value={forgotEmail}
                            onChange={handleForgotChange}
                            required
                            disabled={loading}
                        />
                    </div>
                )}

                <button type="submit" className="btn primary auth-panel__submit" disabled={loading}>
                    {loading ? "处理中..." : isLogin ? "登录" : isRegister ? "注册并开始" : "发送重置邮件"}
                </button>
            </form>

            {isLogin && (
                <div className="auth-panel__extra">
                    <button type="button" className="auth-panel__link" onClick={() => switchMode(MODES.FORGOT)} disabled={loading}>
                        忘记密码？
                    </button>
                </div>
            )}

            <footer className="auth-panel__switch">
                {isLogin && (
                    <>
                        <span>还没有账号？</span>
                        <button type="button" className="auth-panel__link" onClick={() => switchMode(MODES.REGISTER)} disabled={loading}>
                            立即注册
                        </button>
                    </>
                )}
                {isRegister && (
                    <>
                        <span>已经拥有账号？</span>
                        <button type="button" className="auth-panel__link" onClick={() => switchMode(MODES.LOGIN)} disabled={loading}>
                            返回登录
                        </button>
                    </>
                )}
                {isForgot && (
                    <>
                        <span>记起密码了？</span>
                        <button type="button" className="auth-panel__link" onClick={() => switchMode(MODES.LOGIN)} disabled={loading}>
                            返回登录
                        </button>
                    </>
                )}
            </footer>
        </div>
    );
};

export default AuthForm;
